import { useEffect, useState } from "react";
import { Loader2, Check } from "lucide-react";
import { api } from "@/lib/api";

/**
 * eBay category suggestion panel — shows "Recently picked" + live
 * suggestions from eBay. Extracted verbatim from DraftCreator.jsx.
 */
export default function CategorySuggestions({ title, selectedId, onPick }) {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [lastQuery, setLastQuery] = useState("");
  const [recents, setRecents] = useState([]);

  const t = (title || "").trim();

  // Load recently-picked categories once on mount.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/ebay/recent-categories", { params: { limit: 5 } });
        if (!cancelled) setRecents(data?.items || []);
      } catch {
        /* silent */
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!t || t.length < 3) {
      setSuggestions([]);
      return;
    }
    const timer = setTimeout(async () => {
      if (t === lastQuery) return;
      setLoading(true);
      try {
        const { data } = await api.get("/ebay/category-suggest", { params: { q: t } });
        setSuggestions(data?.suggestions || []);
        setLastQuery(t);
      } catch (e) {
        setSuggestions([]);
      } finally {
        setLoading(false);
      }
    }, 400);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t]);

  const pickAndRecord = async (s) => {
    onPick(s.category_id);
    try {
      await api.post("/ebay/recent-categories", {
        category_id: s.category_id,
        name: s.name || "",
        path: s.path || "",
      });
      setRecents((prev) => {
        const filtered = prev.filter((r) => r.category_id !== s.category_id);
        return [
          { category_id: s.category_id, name: s.name || "", path: s.path || "", picked_count: (prev.find((r) => r.category_id === s.category_id)?.picked_count || 0) + 1 },
          ...filtered,
        ].slice(0, 5);
      });
    } catch {
      /* silent — pick still worked locally */
    }
  };

  const recentIds = new Set(recents.map((r) => r.category_id));
  const filteredSuggestions = suggestions.filter((s) => !recentIds.has(s.category_id));

  const showRecents = recents.length > 0;
  const showSuggestions = suggestions.length > 0;
  const showSpinner = loading && suggestions.length === 0;

  if (!showRecents && !showSuggestions && !showSpinner && (!t || t.length < 3)) return null;

  return (
    <div className="mt-2 space-y-2" data-testid="category-suggestions">
      {showRecents && (
        <div data-testid="recent-categories">
          <p className="mb-1 text-[10px] font-mono uppercase tracking-wider text-slate-500">
            Recently picked
          </p>
          <div className="flex flex-col gap-1">
            {recents.map((r) => (
              <SuggestionPill
                key={`recent-${r.category_id}`}
                item={{
                  category_id: r.category_id,
                  name: r.name || `Category #${r.category_id}`,
                  path: r.path || "",
                }}
                variant="recent"
                selected={selectedId === r.category_id}
                onClick={() => pickAndRecord({ category_id: r.category_id, name: r.name, path: r.path })}
                badgeLabel={r.picked_count > 1 ? `${r.picked_count}×` : "★"}
              />
            ))}
          </div>
        </div>
      )}

      {(showSuggestions || showSpinner) && t && t.length >= 3 && (
        <div>
          <p className="mb-1 flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider text-slate-500">
            Suggested by eBay
            {loading && <Loader2 className="h-2.5 w-2.5 animate-spin" />}
          </p>
          {showSpinner ? (
            <div className="flex items-center gap-1 text-[11px] text-slate-500">
              <Loader2 className="h-3 w-3 animate-spin" /> Asking eBay for category matches…
            </div>
          ) : (
            <div className="flex flex-col gap-1">
              {filteredSuggestions.map((s) => (
                <SuggestionPill
                  key={s.category_id}
                  item={s}
                  variant={s.rank === 1 ? "top" : "normal"}
                  selected={selectedId === s.category_id}
                  onClick={() => pickAndRecord(s)}
                  badgeLabel={s.rank}
                  topLabel={s.rank === 1 ? "Best match" : undefined}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function SuggestionPill({ item, variant, selected, onClick, badgeLabel, topLabel }) {
  const containerClass = selected
    ? "border-emerald-400 bg-emerald-50"
    : variant === "recent"
      ? "border-slate-300 bg-white hover:border-slate-400 hover:bg-slate-50"
      : variant === "top"
        ? "border-[#0053DA]/40 bg-[#0053DA]/5 hover:border-[#0053DA] hover:bg-[#0053DA]/10"
        : "border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50";
  const badgeClass = selected
    ? "bg-emerald-600 text-white"
    : variant === "recent"
      ? "bg-slate-800 text-white"
      : variant === "top"
        ? "bg-[#0053DA] text-white"
        : "bg-slate-200 text-slate-600";
  return (
    <button
      type="button"
      onClick={onClick}
      className={`group flex items-start gap-2 rounded-md border px-2 py-1.5 text-left transition-colors ${containerClass}`}
      data-testid={`category-suggestion-${item.category_id}`}
    >
      <span
        className={`inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[10px] font-bold ${badgeClass}`}
      >
        {selected ? <Check className="h-3 w-3" /> : badgeLabel}
      </span>
      <span className="min-w-0 flex-1">
        <span className="flex flex-wrap items-baseline gap-1.5">
          <span className={`text-xs font-semibold ${selected ? "text-emerald-800" : "text-slate-900"}`}>
            {item.name}
          </span>
          <span className="font-mono text-[10px] text-slate-400">#{item.category_id}</span>
          {topLabel && !selected && (
            <span className="rounded-full bg-[#0053DA] px-1.5 py-0 text-[9px] font-semibold uppercase tracking-wider text-white">
              {topLabel}
            </span>
          )}
        </span>
        {item.path && (
          <span className="mt-0.5 block truncate text-[10px] text-slate-500" title={item.path}>
            {item.path}
          </span>
        )}
      </span>
    </button>
  );
}
